import { useState, useMemo } from 'react';
import { getAnonymousPUIEmailTemplate } from '@shared/email/templates/anonymousPUITemplate';
import { generateAnonymousIdentifier } from '../util/generateidentifier';

interface UsePuiEmailReviewProps {
  intakeSections: FormattedIntakeSection[];
  partnerIntakeSections?: FormattedIntakeSection[];
  products: string[];
}

export function usePuiEmailReview({
  intakeSections: initialIntakeSections,
  partnerIntakeSections: initialPartnerIntakeSections,
  products,
}: UsePuiEmailReviewProps) {
  const [anonymousIdentifier, setAnonymousIdentifier] = useState(generateAnonymousIdentifier());
  const [intakeSections, setIntakeSections] =
    useState<FormattedIntakeSection[]>(initialIntakeSections);
  const [partnerIntakeSections, setPartnerIntakeSections] = useState<
    FormattedIntakeSection[] | undefined
  >(initialPartnerIntakeSections);
  const [customMessages, setCustomMessages] = useState<CustomPUIMessages>({});

  const hasPartner = !!initialPartnerIntakeSections;
  const productName = products[0] || 'Long Term Care Insurance';
  const carrierCount = products.length;
  const carrierText = carrierCount === 1 ? 'Carrier' : 'Carriers';

  const individualPreviewHtml = useMemo(() => {
    return getAnonymousPUIEmailTemplate({
      anonymousIdentifier,
      productName,
      intakeSections,
      partnerIntakeSections: undefined,
      customMessages: Object.keys(customMessages).length > 0 ? customMessages : undefined,
    });
  }, [anonymousIdentifier, productName, intakeSections, customMessages]);

  const jointPreviewHtml = useMemo(() => {
    if (!partnerIntakeSections || partnerIntakeSections.length === 0) return null;
    return getAnonymousPUIEmailTemplate({
      anonymousIdentifier,
      productName,
      intakeSections,
      partnerIntakeSections,
      customMessages: Object.keys(customMessages).length > 0 ? customMessages : undefined,
    });
  }, [anonymousIdentifier, productName, intakeSections, partnerIntakeSections, customMessages]);

  const handleDeleteSection = (sectionIndex: number, isPartner: boolean = false) => {
    if (isPartner && partnerIntakeSections) {
      setPartnerIntakeSections(partnerIntakeSections.filter((_, i) => i !== sectionIndex));
    } else {
      setIntakeSections(intakeSections.filter((_, i) => i !== sectionIndex));
    }
  };

  const handleDeleteItem = (
    sectionIndex: number,
    itemIndex: number,
    isPartner: boolean = false,
  ) => {
    if (isPartner && partnerIntakeSections) {
      const updated = [...partnerIntakeSections];
      updated[sectionIndex].items = updated[sectionIndex].items.filter((_, i) => i !== itemIndex);
      setPartnerIntakeSections(updated);
    } else {
      const updated = [...intakeSections];
      updated[sectionIndex].items = updated[sectionIndex].items.filter((_, i) => i !== itemIndex);
      setIntakeSections(updated);
    }
  };

  const handleEditItemValue = (
    sectionIndex: number,
    itemIndex: number,
    newValue: string,
    isPartner: boolean = false,
  ) => {
    if (isPartner && partnerIntakeSections) {
      const updated = [...partnerIntakeSections];
      updated[sectionIndex].items[itemIndex].value = newValue;
      setPartnerIntakeSections(updated);
    } else {
      const updated = [...intakeSections];
      updated[sectionIndex].items[itemIndex].value = newValue;
      setIntakeSections(updated);
    }
  };

  const handleEditItemLabel = (
    sectionIndex: number,
    itemIndex: number,
    newLabel: string,
    isPartner: boolean = false,
  ) => {
    if (isPartner && partnerIntakeSections) {
      const updated = [...partnerIntakeSections];
      updated[sectionIndex].items[itemIndex].label = newLabel;
      setPartnerIntakeSections(updated);
    } else {
      const updated = [...intakeSections];
      updated[sectionIndex].items[itemIndex].label = newLabel;
      setIntakeSections(updated);
    }
  };

  const handleEditSectionCategory = (
    sectionIndex: number,
    newCategory: string,
    isPartner: boolean = false,
  ) => {
    if (isPartner && partnerIntakeSections) {
      const updated = [...partnerIntakeSections];
      updated[sectionIndex].category = newCategory;
      setPartnerIntakeSections(updated);
    } else {
      const updated = [...intakeSections];
      updated[sectionIndex].category = newCategory;
      setIntakeSections(updated);
    }
  };

  const handleAddItem = (sectionIndex: number, isPartner: boolean = false) => {
    const newItem = { label: 'New Field', value: '' };
    if (isPartner && partnerIntakeSections) {
      const updated = [...partnerIntakeSections];
      updated[sectionIndex].items.push(newItem);
      setPartnerIntakeSections(updated);
    } else {
      const updated = [...intakeSections];
      updated[sectionIndex].items.push(newItem);
      setIntakeSections(updated);
    }
  };

  const handleAddSection = (isPartner: boolean = false) => {
    const newSection: FormattedIntakeSection = {
      category: 'New Section',
      items: [{ label: 'Field', value: '' }],
      hasData: true,
    };
    if (isPartner) {
      setPartnerIntakeSections([...(partnerIntakeSections || []), newSection]);
    } else {
      setIntakeSections([...intakeSections, newSection]);
    }
  };

  return {
    anonymousIdentifier,
    setAnonymousIdentifier,
    intakeSections,
    partnerIntakeSections,
    customMessages,
    setCustomMessages,
    hasPartner,
    productName,
    carrierCount,
    carrierText,
    individualPreviewHtml,
    jointPreviewHtml,
    handleDeleteSection,
    handleDeleteItem,
    handleEditItemValue,
    handleEditItemLabel,
    handleEditSectionCategory,
    handleAddItem,
    handleAddSection,
  };
}